import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Icon,
  Image,
  Text,
  VStack,
  SimpleGrid,
  useColorModeValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import {
  FaChartLine,
  FaUsers,
  FaFileInvoice,
  FaCalendar,
  FaDatabase,
  FaUserFriends,
  FaMailBulk,
} from 'react-icons/fa';
import { ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';

import heroImage01 from '../assets/hero-7.jpeg';
import heroImage02 from '../assets/hero-06.jpg';
import { IconProps } from '@chakra-ui/react';
import TrustedByBusinesses from '../components/TrustedByBusinesses';
import Footer from '../components/layout/footer';

const features: {
  icon: ComponentType<IconProps>;
  title: string;
  description: string;
}[] = [
  {
    icon: FaMailBulk as ComponentType<IconProps>,
    title: 'A Unified Inbox',
    description:
      'Get Your WhatsApp, Facebook and Instagram customer conversations all in one place! Reduces drag and faster responses with AI powered responses.',
  },
  {
    icon: FaCalendar as ComponentType<IconProps>,
    title: 'Smart AI-Powered Follow-up Reminders',
    description:
      'Never miss a deal again - get intelligent reminders that adapt to customer engagement patterns and suggest the best time to reach out.',
  },
  {
    icon: FaUsers as ComponentType<IconProps>,
    title: 'Lead Management with AI Insights',
    description:
      'Identify high-value leads instantly with AI-driven scoring, next-step recommendations, and predictive deal-closing insights.',
  },
  {
    icon: FaDatabase as ComponentType<IconProps>,
    title: 'Centralized Customer Data',
    description:
      'Keep all contacts, conversations, and interests in one place - a single source of truth that eliminates scattered spreadsheets and lost opportunities.',
  },
  {
    icon: FaChartLine as ComponentType<IconProps>,
    title: 'Leads & Sales Analytics',
    description:
      'Turn raw data into action with AI-powered analytics, conversion funnels, and performance dashboards to forecast revenue with confidence.',
  },
  {
    icon: FaUserFriends as ComponentType<IconProps>,
    title: 'Sales Team Collaboration',
    description:
      'Assign tasks, track progress, and collaborate in real time. AI ensures the right teammate gets the right lead at the right time.',
  },
  {
    icon: FaFileInvoice as ComponentType<IconProps>,
    title: 'Generate Professional Quotes',
    description:
      'Create and share polished, branded quotes in seconds - tailored to customer needs and optimized for faster deal closure.',
  },
];

const MotionBox = motion(Box);

const LandingPage = () => {
  const navigate = useNavigate();
  const bgFeatureCard = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.600', 'gray.300');

  return (
    <Box
      bg="#f9f9f9"
      color="#1a1a1a"
      fontFamily="Inter, sans-serif"
      flexDirection={'column'}
    >
      <Box bg="#009800ff" py={{ base: 14, md: 20 }} color="white">
        <Container maxW="6xl">
          <Flex
            // direction={{ base: 'column', md: 'row' }}
            direction={'row'}
            flexWrap={'wrap'}
            alignItems="center"
            justifyContent={'center'}
            gap={10}
          >
            <VStack align="start" spacing={6} flex={1}>
              <MotionBox
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Heading fontSize="3xl" lineHeight="1.2">
                  Let AI manage your customers efficiently!
                </Heading>
                <Heading fontSize="xl" mt={5}>
                  Built for scale. Powered by AI.
                </Heading>
              </MotionBox>
              <Text fontSize="lg" opacity={0.9}>
                Your WhatsApp, Facebook and Instagram Inboxes in one place!
              </Text>
              <Button
                size="lg"
                onClick={() => navigate('sign-up')}
                bg="white"
                color="#009800ff"
                _hover={{ bg: '#e6ffe6' }}
              >
                Get Started
              </Button>
            </VStack>
            <Image
              src={heroImage01}
              alt="CRM AI Illustration"
              // flex={1}
              h="310px"
              objectFit="cover"
              alignSelf={'center'}
              borderRadius="xl"
              boxShadow="xl"
            />
          </Flex>
        </Container>
      </Box>

      {/* Features Section */}
      <Box py={4}>
        <Container maxW="6xl">
          <Heading
            color={'#000'}
            fontSize={'2xl'}
            textAlign={'center'}
            mb={5}
            mt={5}
          >
            Features
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={10}>
            {features.map((feature, idx) => (
              <Box
                key={idx}
                p={6}
                bg={bgFeatureCard}
                boxShadow="xl"
                rounded="2xl"
                transition="all 0.3s"
                _hover={{ transform: 'translateY(-5px)', boxShadow: '2xl' }}
              >
                <Icon
                  as={feature.icon}
                  w={10}
                  h={10}
                  color="#009800ff"
                  mb={4}
                />
                <Heading size="md" mb={2}>
                  {feature.title}
                </Heading>
                <Text color={textColor} fontSize="sm">
                  {feature.description}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>
      <TrustedByBusinesses />
      {/* Value Proposition Section */}
      <Box bg="#e6ffe6" py={20}>
        <Container maxW="6xl">
          <Heading mb={8} textAlign="center" fontWeight="semibold">
            Built to Help you Close More Sales, Faster!
          </Heading>
          <SimpleGrid
            columns={{ base: 1, md: 2 }}
            spacing={12}
            alignItems="center"
          >
            <VStack align="start" spacing={6}>
              <Text fontSize="md" color="gray.700">
                Leverage AI to prioritize leads, predict next best actions, and
                automate chat responses.
              </Text>
              <Text fontSize="md" color="gray.700">
                Reduce lead loss, increase follow-ups, and accelerate deal
                closures for higher conversion rates across your sales pipeline.
              </Text>
              <Text fontSize="md" color="gray.700">
                Save time with our automated workflows.
              </Text>
              <Button
                size="lg"
                bg="#009800ff"
                color="white"
                _hover={{ bg: '#009800e0' }}
                onClick={() => navigate('sign-up')}
              >
                Create Account
              </Button>
            </VStack>
            <Image
              src={heroImage02}
              alt="AI CRM Flow"
              borderRadius="xl"
              boxShadow="lg"
            />
          </SimpleGrid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box bg="#009800ff" py={16} color="white">
        <Container maxW="4xl" textAlign="center">
          <Heading fontSize="2xl" mb={4}>
            Ready to Experience Efficient Customer Management?
          </Heading>
          <Text fontSize="lg" mb={6} opacity={0.9}>
            Experience smarter sales management, AI-powered insights, and a
            unified workflow designed to boost your conversion rates.
          </Text>
          <Button
            size="lg"
            bg="white"
            color="#009800ff"
            _hover={{ bg: '#e6ffe6' }}
            onClick={() => navigate('sign-up')}
          >
            Create Account Now
          </Button>
        </Container>
      </Box>

      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default LandingPage;
